import notificationService from '../services/notificationService';

const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
};

export const isPushSupported = () =>
    'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

// navigator.serviceWorker.ready resolves as soon as ANY worker is active for the
// page's scope - not necessarily the one we just registered. Waiting on that alone
// can let pushManager.subscribe() run against a registration that's still
// installing, which throws "no active Service Worker". Wait on this specific
// registration's worker instead.
const waitForActiveWorker = (registration) => {
    if (registration.active) return Promise.resolve();

    const worker = registration.installing || registration.waiting;
    if (!worker) return navigator.serviceWorker.ready.then(() => {});

    return new Promise((resolve) => {
        const handler = () => {
            if (worker.state === 'activated') {
                worker.removeEventListener('statechange', handler);
                resolve();
            }
        };
        worker.addEventListener('statechange', handler);
        // The worker may have already activated in the moment between reading
        // installing/waiting above and attaching this listener - the
        // 'statechange' event for that transition would never reach us, and
        // this promise would hang forever. Check the current state directly too.
        handler();
    });
};

export const getExistingPushSubscription = async () => {
    if (!isPushSupported()) return null;
    // No argument defaults to the current page's URL, matched against each
    // registration's scope - the documented way to look this up. Passing the
    // service worker's own script URL here (as this used to) happens to work
    // by accident, but breaks once a broken/half-installed registration is
    // left behind by a failed registration attempt.
    const registration = await navigator.serviceWorker.getRegistration('/');
    if (!registration || !registration.active) return null;
    return registration.pushManager.getSubscription();
};

// Chrome has a well-documented, still-unfixed bug where the very first
// serviceWorker.register() call on a site can throw
// "AbortError: Registration failed - storage error" because its internal
// Service Worker storage briefly isn't ready. It reliably succeeds on a
// second attempt a moment later, so retry once instead of surfacing this to
// the user as a real failure.
const registerWithRetry = async (retriesLeft = 2) => {
    try {
        return await navigator.serviceWorker.register('/push-sw.js', { updateViaCache: 'none' });
    } catch (error) {
        const isStorageGlitch = error?.name === 'AbortError' && /storage error/i.test(error?.message || '');
        if (isStorageGlitch && retriesLeft > 0) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            return registerWithRetry(retriesLeft - 1);
        }
        throw error;
    }
};

export const subscribeToPush = async () => {
    if (!isPushSupported()) {
        throw new Error('Push notifications are not supported in this browser');
    }

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
        throw new Error('Notification permission was not granted');
    }

    // A previous failed attempt (e.g. Chrome's "storage error" bug) can leave a
    // broken registration with no active worker behind. Clear it out before
    // trying again so we always register against a clean slate.
    const existingRegistration = await navigator.serviceWorker.getRegistration('/');
    if (existingRegistration && !existingRegistration.active) {
        await existingRegistration.unregister();
    }

    const registration = await registerWithRetry();
    await waitForActiveWorker(registration);

    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
        const publicKey = await notificationService.getVapidPublicKey();
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(publicKey)
        });
    }

    await notificationService.savePushSubscription(subscription.toJSON());
    return subscription;
};

export const unsubscribeFromPush = async () => {
    const subscription = await getExistingPushSubscription();
    if (!subscription) return;

    await notificationService.deletePushSubscription(subscription.endpoint);
    await subscription.unsubscribe();
};
